import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Phone, ChevronDown, Linkedin, Facebook, Youtube, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import UnifiedContactModal from './UnifiedContactModal';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showSulfurDropdown, setShowSulfurDropdown] = useState(false);
  const [showHydroDropdown, setShowHydroDropdown] = useState(false);
  const [showFiltrationDropdown, setShowFiltrationDropdown] = useState(false);
  const [showIndustriesDropdown, setShowIndustriesDropdown] = useState(false);
  const [showInlineSubDropdown, setShowInlineSubDropdown] = useState(false);
  const [showSuctionSubDropdown, setShowSuctionSubDropdown] = useState(false);
  const [showWaterIntakeSubDropdown, setShowWaterIntakeSubDropdown] = useState(false);
  const [mobileSulfurOpen, setMobileSulfurOpen] = useState(false);
  const [mobileHydroOpen, setMobileHydroOpen] = useState(false);
  const [mobileFiltrationOpen, setMobileFiltrationOpen] = useState(false);
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const sulfurDropdownRef = useRef<HTMLDivElement>(null);
  const hydroDropdownRef = useRef<HTMLDivElement>(null);
  const filtrationDropdownRef = useRef<HTMLDivElement>(null);
  const industriesDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowSulfurDropdown(false);
        setShowHydroDropdown(false);
        setShowFiltrationDropdown(false);
        setShowIndustriesDropdown(false);
      }
      // Check if click is outside any dropdown
      const clickedOutsideSulfur = showSulfurDropdown && sulfurDropdownRef.current && !sulfurDropdownRef.current.contains(event.target as Node);
      const clickedOutsideHydro = showHydroDropdown && hydroDropdownRef.current && !hydroDropdownRef.current.contains(event.target as Node);
      const clickedOutsideFiltration = showFiltrationDropdown && filtrationDropdownRef.current && !filtrationDropdownRef.current.contains(event.target as Node);
      const clickedOutsideIndustries = showIndustriesDropdown && industriesDropdownRef.current && !industriesDropdownRef.current.contains(event.target as Node);

      if (clickedOutsideSulfur) {
        setShowSulfurDropdown(false);
      }
      if (clickedOutsideHydro) {
        setShowHydroDropdown(false);
      }
      if (clickedOutsideFiltration) {
        setShowFiltrationDropdown(false);
      }
      if (clickedOutsideIndustries) {
        setShowIndustriesDropdown(false);
      }
    };

    if (isOpen || showSulfurDropdown || showHydroDropdown || showFiltrationDropdown || showIndustriesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, showSulfurDropdown, showHydroDropdown, showFiltrationDropdown, showIndustriesDropdown]);

  // Desktop dropdown hover handlers
  const handleSulfurDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setShowHydroDropdown(false);
    setShowFiltrationDropdown(false);
    setShowIndustriesDropdown(false);
    setShowSulfurDropdown(true);
  };

  const handleSulfurDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowSulfurDropdown(false);
    }, 150);
  };
  
  const handleHydroDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setShowSulfurDropdown(false);
    setShowFiltrationDropdown(false);
    setShowIndustriesDropdown(false);
    setShowHydroDropdown(true);
  };

  const handleHydroDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowHydroDropdown(false);
    }, 150);
  };

  const handleFiltrationDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setShowSulfurDropdown(false);
    setShowHydroDropdown(false);
    setShowIndustriesDropdown(false);
    setShowFiltrationDropdown(true);
  };

  const handleFiltrationDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowFiltrationDropdown(false);
    }, 150);
  };

  const handleIndustriesDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setShowSulfurDropdown(false);
    setShowHydroDropdown(false);
    setShowFiltrationDropdown(false);
    setShowIndustriesDropdown(true);
  };

  const handleIndustriesDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowIndustriesDropdown(false);
    }, 150);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
    };
  }, []);

  // Force check logo availability on mount
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      console.log('✅ Navbar logo preload successful');
      setLogoLoaded(true);
      setLogoError(false);
    };
    img.onerror = () => {
      console.error('❌ Navbar logo preload failed');
      setLogoError(true);
      setLogoLoaded(false);
    };
    img.src = '/ctc_logo_26 copy.png';
  }, []);

  const handleLogoError = () => {
    const logoPath = '/ctc_logo_26 copy.png';
    console.error('❌ Navbar logo failed to load in img element');
    console.log(`Current URL: ${window.location.href}`);
    console.log(`Logo URL: ${window.location.origin}${logoPath}`);
    console.log(`User Agent: ${navigator.userAgent}`);
    setLogoError(true);
  };

  const handleLogoLoad = () => {
    console.log('✅ Navbar logo loaded successfully in img element');
    setLogoError(false);
    setLogoLoaded(true);
  };

  const PhoneNumber = ({ className, size }: { className?: string, size?: number }) => {
    const phoneNumber = "(509) 901-7476";

    if (isMobile) {
      return (
        <a
          href="tel:+15099017476"
          className={`text-green-600 font-bold hover:text-green-700 transition-colors ${className || ''}`}
        >
          {phoneNumber}
        </a>
      );
    }

    return (
      <div className={`text-green-600 font-bold ${className || ''}`}>
        {phoneNumber}
      </div>
    );
  };

  return (
    <motion.nav
      ref={mobileMenuRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b shadow-lg bg-white/95 border-ctc-silver"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              {!logoError ? (
                <img
                  src="/ctc_logo_26 copy.png"
                  alt="CTC Waterworks"
                  className="h-12 w-auto"
                  onError={handleLogoError}
                  onLoad={handleLogoLoad}
                  style={{
                    maxWidth: '324px',
                    height: 'auto'
                  }}
                />
              ) : (
                <div className="h-10 flex items-center bg-gradient-to-r from-ctc-blue to-ctc-blue-dark px-3 py-1 rounded">
                  <span className="text-xl font-bold text-white tracking-tight">
                    CTC Waterworks
                  </span>
                </div>
              )}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-3">
            {/* Social Media Links */}
            <div className="flex items-center space-x-1 mr-1 -ml-8">
              <a
                href="https://www.linkedin.com/company/ctc-waterworks/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-ctc-blue/10 rounded-lg flex items-center justify-center hover:bg-ctc-blue hover:scale-110 transition-all duration-300 group"
                aria-label="Follow CTC Waterworks on LinkedIn"
              >
                <Linkedin className="text-ctc-blue group-hover:text-white" size={16} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?viewas=100000686899395&id=61577642326281"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-blue-600/10 rounded-lg flex items-center justify-center hover:bg-blue-600 hover:scale-110 transition-all duration-300 group"
                aria-label="Follow CTC Waterworks on Facebook"
              >
                <Facebook className="text-blue-500 group-hover:text-white" size={16} />
              </a>
              <a
                href="https://www.youtube.com/@CTCWaterworks"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-red-600/10 rounded-lg flex items-center justify-center hover:bg-red-600 hover:scale-110 transition-all duration-300 group"
                aria-label="Subscribe to CTC Waterworks on YouTube"
              >
                <Youtube className="text-red-500 group-hover:text-white" size={16} />
              </a>
              <a
                href="https://www.instagram.com/ctcwaterworks"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-pink-600/10 rounded-lg flex items-center justify-center hover:bg-pink-600 hover:scale-110 transition-all duration-300 group"
                aria-label="Follow CTC Waterworks on Instagram"
              >
                <Instagram className="text-pink-500 group-hover:text-white" size={16} />
              </a>
            </div>

            {/* Sulfur Burners Dropdown */}
            <div className="relative" ref={sulfurDropdownRef}>
              <Link
                to="/sulfur-burners"
                onMouseEnter={handleSulfurDropdownEnter}
                onMouseLeave={handleSulfurDropdownLeave}
                className="flex items-center space-x-1 text-gray-700 hover:text-ctc-blue transition-colors font-medium py-2 text-sm whitespace-nowrap"
              >
                <span>Sulfur Burners</span>
                <ChevronDown size={16} className={`transition-transform ${showSulfurDropdown ? 'rotate-180' : ''}`} />
              </Link>
              
              {showSulfurDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  onMouseEnter={handleSulfurDropdownEnter}
                  onMouseLeave={handleSulfurDropdownLeave}
                  className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                  style={{ 
                    paddingTop: '4px',
                    marginTop: '-4px'
                  }}
                >
                  <Link
                    to="/sulfur-burners"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors font-medium"
                    onClick={() => setShowSulfurDropdown(false)}
                  >
                    Overview
                  </Link>
                  <Link
                    to="/sulfur-burner-guide"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 font-medium"
                    onClick={() => setShowSulfurDropdown(false)}
                  >
                    Better Water Guide
                  </Link>
                  <Link
                    to="/research"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 font-medium"
                    onClick={() => setShowSulfurDropdown(false)}
                  >
                    Research
                  </Link>
                </motion.div>
              )}
            </div>
            
            {/* Hydrocoolers Dropdown */}
            <div className="relative" ref={hydroDropdownRef}>
              <Link
                to="/hydrocoolers"
                onMouseEnter={handleHydroDropdownEnter}
                onMouseLeave={handleHydroDropdownLeave}
                className="flex items-center space-x-1 text-gray-700 hover:text-ctc-blue transition-colors font-medium py-2 text-sm whitespace-nowrap"
              >
                <span>Hydrocoolers</span>
                <ChevronDown size={16} className={`transition-transform ${showHydroDropdown ? 'rotate-180' : ''}`} />
              </Link>
              
              {showHydroDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  onMouseEnter={handleHydroDropdownEnter}
                  onMouseLeave={handleHydroDropdownLeave}
                  className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                  style={{
                    paddingTop: '4px',
                    marginTop: '-4px'
                  }}
                >
                  <Link
                    to="/hydrocoolers"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors font-medium"
                    onClick={() => setShowHydroDropdown(false)}
                  >
                    Overview
                  </Link>
                  <Link
                    to="/cherry-hydrocoolers"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 font-medium"
                    onClick={() => setShowHydroDropdown(false)}
                  >
                    Cherry Hydrocoolers
                  </Link>
                  <Link
                    to="/portable-hydrocoolers"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 font-medium"
                    onClick={() => setShowHydroDropdown(false)}
                  >
                    Portable Hydrocoolers
                  </Link>
                </motion.div>
              )}
            </div>
            
            <Link to="/dpa-drenchers" className="text-gray-700 hover:text-ctc-blue transition-colors font-medium text-sm whitespace-nowrap">
              DPA Drenchers
            </Link>

            {/* Filtration Dropdown */}
            <div className="relative" ref={filtrationDropdownRef}>
              <Link
                to="/filtration"
                onMouseEnter={handleFiltrationDropdownEnter}
                onMouseLeave={handleFiltrationDropdownLeave}
                className="flex items-center space-x-1 text-gray-700 hover:text-ctc-blue transition-colors font-medium py-2 text-sm whitespace-nowrap"
              >
                <span>Filtration</span>
                <ChevronDown size={16} className={`transition-transform ${showFiltrationDropdown ? 'rotate-180' : ''}`} />
              </Link>

              {showFiltrationDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  onMouseEnter={handleFiltrationDropdownEnter}
                  onMouseLeave={handleFiltrationDropdownLeave}
                  className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                  style={{
                    paddingTop: '4px',
                    marginTop: '-4px'
                  }}
                >
                  <Link
                    to="/flood-resilience"
                    className="block px-4 py-3 text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors font-semibold border-b border-gray-100"
                    onClick={() => setShowFiltrationDropdown(false)}
                  >
                    Floods & Dirty Water
                  </Link>
                  <Link
                    to="/filtration"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors font-medium"
                    onClick={() => setShowFiltrationDropdown(false)}
                  >
                    Overview
                  </Link>

                  {/* Inline Filters Sub-dropdown */}
                  <div className="relative group border-t border-gray-100">
                    <div
                      className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors font-medium cursor-pointer"
                      onMouseEnter={() => setShowInlineSubDropdown(true)}
                      onMouseLeave={() => setShowInlineSubDropdown(false)}
                    >
                      <Link to="/filtration/inline" className="flex-1">Inline Filters</Link>
                      <ChevronDown size={14} className="transform -rotate-90" />
                    </div>
                    {showInlineSubDropdown && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        onMouseEnter={() => setShowInlineSubDropdown(true)}
                        onMouseLeave={() => setShowInlineSubDropdown(false)}
                        className="absolute left-full top-0 ml-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                      >
                        <Link
                          to="/filtration/inline/big"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors text-sm"
                          onClick={() => { setShowFiltrationDropdown(false); setShowInlineSubDropdown(false); }}
                        >
                          BIG Series
                        </Link>
                        <Link
                          to="/filtration/inline/big-s"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 text-sm"
                          onClick={() => { setShowFiltrationDropdown(false); setShowInlineSubDropdown(false); }}
                        >
                          BIG-S Series Stainless
                        </Link>
                        <Link
                          to="/filtration/inline/gng"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 text-sm"
                          onClick={() => { setShowFiltrationDropdown(false); setShowInlineSubDropdown(false); }}
                        >
                          GNG Series
                        </Link>
                        <Link
                          to="/filtration/inline/tig"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 text-sm"
                          onClick={() => { setShowFiltrationDropdown(false); setShowInlineSubDropdown(false); }}
                        >
                          TIG Series
                        </Link>
                        <Link
                          to="/filtration/inline/cleaning"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 text-sm font-semibold"
                          onClick={() => { setShowFiltrationDropdown(false); setShowInlineSubDropdown(false); }}
                        >
                          Cleaning Options
                        </Link>
                      </motion.div>
                    )}
                  </div>

                  {/* Suction Screens Sub-dropdown */}
                  <div className="relative group border-t border-gray-100">
                    <div
                      className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors font-medium cursor-pointer"
                      onMouseEnter={() => setShowSuctionSubDropdown(true)}
                      onMouseLeave={() => setShowSuctionSubDropdown(false)}
                    >
                      <Link to="/filtration/suction-screens" className="flex-1">Suction Screens</Link>
                      <ChevronDown size={14} className="transform -rotate-90" />
                    </div>
                    {showSuctionSubDropdown && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        onMouseEnter={() => setShowSuctionSubDropdown(true)}
                        onMouseLeave={() => setShowSuctionSubDropdown(false)}
                        className="absolute left-full top-0 ml-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                      >
                        <Link
                          to="/filtration/suction-screens/standard"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors text-sm"
                          onClick={() => { setShowFiltrationDropdown(false); setShowSuctionSubDropdown(false); }}
                        >
                          Standard
                        </Link>
                        <Link
                          to="/filtration/suction-screens/self-cleaning"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 text-sm"
                          onClick={() => { setShowFiltrationDropdown(false); setShowSuctionSubDropdown(false); }}
                        >
                          Self-Cleaning
                        </Link>
                        <Link
                          to="/filtration/suction-screens/passive-fish-screens"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 text-sm"
                          onClick={() => { setShowFiltrationDropdown(false); setShowSuctionSubDropdown(false); }}
                        >
                          Passive Fish Screens
                        </Link>
                      </motion.div>
                    )}
                  </div>

                  {/* Water Intake Sub-dropdown */}
                  <div className="relative group border-t border-gray-100">
                    <div
                      className="flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors font-medium cursor-pointer"
                      onMouseEnter={() => setShowWaterIntakeSubDropdown(true)}
                      onMouseLeave={() => setShowWaterIntakeSubDropdown(false)}
                    >
                      <Link to="/filtration/water-intake" className="flex-1">Water Box & Specialty</Link>
                      <ChevronDown size={14} className="transform -rotate-90" />
                    </div>
                    {showWaterIntakeSubDropdown && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        onMouseEnter={() => setShowWaterIntakeSubDropdown(true)}
                        onMouseLeave={() => setShowWaterIntakeSubDropdown(false)}
                        className="absolute left-full top-0 ml-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                      >
                        <Link
                          to="/filtration/water-intake/water-box"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors text-sm"
                          onClick={() => { setShowFiltrationDropdown(false); setShowWaterIntakeSubDropdown(false); }}
                        >
                          Water Box
                        </Link>
                        <Link
                          to="/filtration/water-intake/basket-with-bag"
                          className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100 text-sm"
                          onClick={() => { setShowFiltrationDropdown(false); setShowWaterIntakeSubDropdown(false); }}
                        >
                          Basket with Bag
                        </Link>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Industries Dropdown */}
            <div className="relative" ref={industriesDropdownRef}>
              <Link
                to="/industries"
                onMouseEnter={handleIndustriesDropdownEnter}
                onMouseLeave={handleIndustriesDropdownLeave}
                className="flex items-center space-x-1 text-gray-700 hover:text-ctc-blue transition-colors font-medium py-2 text-sm whitespace-nowrap"
              >
                <span>Industries</span>
                <ChevronDown size={16} className={`transition-transform ${showIndustriesDropdown ? 'rotate-180' : ''}`} />
              </Link>

              {showIndustriesDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  onMouseEnter={handleIndustriesDropdownEnter}
                  onMouseLeave={handleIndustriesDropdownLeave}
                  className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
                  style={{
                    paddingTop: '4px',
                    marginTop: '-4px'
                  }}
                >
                  <Link
                    to="/industries"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors font-medium"
                    onClick={() => setShowIndustriesDropdown(false)}
                  >
                    All Industries
                  </Link>
                  <Link
                    to="/industries/agriculture"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100"
                    onClick={() => setShowIndustriesDropdown(false)}
                  >
                    Agriculture
                  </Link>
                  <Link
                    to="/industries/municipal"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100"
                    onClick={() => setShowIndustriesDropdown(false)}
                  >
                    Municipal
                  </Link>
                  <Link
                    to="/industries/industrial"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100"
                    onClick={() => setShowIndustriesDropdown(false)}
                  >
                    Industrial
                  </Link>
                  <Link
                    to="/industries/hydropower"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100"
                    onClick={() => setShowIndustriesDropdown(false)}
                  >
                    Hydropower
                  </Link>
                  <Link
                    to="/industries/mining"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100"
                    onClick={() => setShowIndustriesDropdown(false)}
                  >
                    Mining & Quarrying
                  </Link>
                  <Link
                    to="/industries/aquaculture"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100"
                    onClick={() => setShowIndustriesDropdown(false)}
                  >
                    Aquaculture & Fisheries
                  </Link>
                  <Link
                    to="/industries/environmental"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100"
                    onClick={() => setShowIndustriesDropdown(false)}
                  >
                    Environmental
                  </Link>
                  <Link
                    to="/industries/desalination"
                    className="block px-4 py-3 text-gray-700 hover:bg-ctc-blue/5 hover:text-ctc-blue transition-colors border-t border-gray-100"
                    onClick={() => setShowIndustriesDropdown(false)}
                  >
                    Desalination & Water Reuse
                  </Link>
                </motion.div>
              )}
            </div>

            <Link to="/blog" className="text-gray-700 hover:text-ctc-blue transition-colors font-medium text-sm whitespace-nowrap">
              Blog
            </Link>

            <PhoneNumber className="text-sm whitespace-nowrap" />

            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-3 py-2 rounded-lg hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 shadow-md font-medium text-sm whitespace-nowrap"
            >
              Contact Us
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-gray-700 hover:text-ctc-blue transition-colors"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden border-t border-ctc-silver max-h-[calc(100vh-5rem)] overflow-y-auto"
          >
            <div className="flex flex-col py-4">
              {/* Mobile Social Media Links */}
              <div className="flex justify-center space-x-3 py-3 mb-2 border-b border-ctc-silver/30">
                <a
                  href="https://www.linkedin.com/company/ctc-waterworks/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-ctc-blue/10 rounded-lg flex items-center justify-center hover:bg-ctc-blue transition-all duration-300 group"
                  aria-label="Follow CTC Waterworks on LinkedIn"
                >
                  <Linkedin className="text-ctc-blue group-hover:text-white" size={18} />
                </a>
                <a
                  href="https://www.facebook.com/profile.php?viewas=100000686899395&id=61577642326281"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-blue-600/10 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-all duration-300 group"
                  aria-label="Follow CTC Waterworks on Facebook"
                >
                  <Facebook className="text-blue-500 group-hover:text-white" size={18} />
                </a>
                <a
                  href="https://www.youtube.com/@CTCWaterworks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-red-600/10 rounded-lg flex items-center justify-center hover:bg-red-600 transition-all duration-300 group"
                  aria-label="Subscribe to CTC Waterworks on YouTube"
                >
                  <Youtube className="text-red-500 group-hover:text-white" size={18} />
                </a>
                <a
                  href="https://www.instagram.com/ctcwaterworks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-pink-600/10 rounded-lg flex items-center justify-center hover:bg-pink-600 transition-all duration-300 group"
                  aria-label="Follow CTC Waterworks on Instagram"
                >
                  <Instagram className="text-pink-500 group-hover:text-white" size={18} />
                </a>
              </div>

              {/* Sulfur Burners Accordion */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => setMobileSulfurOpen(!mobileSulfurOpen)}
                  className="w-full flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  <span>Sulfur Burners</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileSulfurOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileSulfurOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-gray-50"
                  >
                    <Link
                      to="/sulfur-burners"
                      onClick={() => { setIsOpen(false); setMobileSulfurOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Overview
                    </Link>
                    <Link
                      to="/sulfur-burner-guide"
                      onClick={() => { setIsOpen(false); setMobileSulfurOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Better Water Guide
                    </Link>
                    <Link
                      to="/research"
                      onClick={() => { setIsOpen(false); setMobileSulfurOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Research
                    </Link>
                  </motion.div>
                )}
              </div>

              {/* Hydrocoolers Accordion */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => setMobileHydroOpen(!mobileHydroOpen)}
                  className="w-full flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  <span>Hydrocoolers</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileHydroOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileHydroOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-gray-50"
                  >
                    <Link
                      to="/hydrocoolers"
                      onClick={() => { setIsOpen(false); setMobileHydroOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Overview
                    </Link>
                    <Link
                      to="/cherry-hydrocoolers"
                      onClick={() => { setIsOpen(false); setMobileHydroOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Cherry Hydrocoolers
                    </Link>
                    <Link
                      to="/portable-hydrocoolers"
                      onClick={() => { setIsOpen(false); setMobileHydroOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Portable Hydrocoolers
                    </Link>
                  </motion.div>
                )}
              </div>

              {/* DPA Drenchers */}
              <Link
                to="/dpa-drenchers"
                onClick={() => setIsOpen(false)}
                className="text-gray-700 hover:bg-gray-50 transition-colors font-medium text-left py-3 px-4 border-b border-gray-100"
              >
                DPA Drenchers
              </Link>

              {/* Filtration Accordion */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => setMobileFiltrationOpen(!mobileFiltrationOpen)}
                  className="w-full flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  <span>Filtration</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileFiltrationOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileFiltrationOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-gray-50"
                  >
                    <Link
                      to="/flood-resilience"
                      onClick={() => { setIsOpen(false); setMobileFiltrationOpen(false); }}
                      className="block text-red-600 hover:text-red-700 hover:bg-white transition-colors py-2.5 px-6 text-sm font-semibold"
                    >
                      Floods & Dirty Water
                    </Link>
                    <Link
                      to="/filtration"
                      onClick={() => { setIsOpen(false); setMobileFiltrationOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Overview
                    </Link>
                    <Link
                      to="/filtration/inline"
                      onClick={() => { setIsOpen(false); setMobileFiltrationOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Inline Filters
                    </Link>
                    <Link
                      to="/filtration/inline/cleaning"
                      onClick={() => { setIsOpen(false); setMobileFiltrationOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm font-semibold"
                    >
                      → Cleaning Options
                    </Link>
                    <Link
                      to="/filtration/suction-screens"
                      onClick={() => { setIsOpen(false); setMobileFiltrationOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Suction Screens
                    </Link>
                    <Link
                      to="/filtration/water-intake"
                      onClick={() => { setIsOpen(false); setMobileFiltrationOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Water Box & Specialty
                    </Link>
                    <Link
                      to="/filtration/parts"
                      onClick={() => { setIsOpen(false); setMobileFiltrationOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Parts
                    </Link>
                  </motion.div>
                )}
              </div>

              {/* Industries Accordion */}
              <div className="border-b border-gray-100">
                <button
                  onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                  className="w-full flex items-center justify-between py-3 px-4 text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  <span>Industries</span>
                  <ChevronDown size={18} className={`transition-transform ${mobileIndustriesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileIndustriesOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="bg-gray-50"
                  >
                    <Link
                      to="/industries"
                      onClick={() => { setIsOpen(false); setMobileIndustriesOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      All Industries
                    </Link>
                    <Link
                      to="/industries/agriculture"
                      onClick={() => { setIsOpen(false); setMobileIndustriesOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Agriculture
                    </Link>
                    <Link
                      to="/industries/municipal"
                      onClick={() => { setIsOpen(false); setMobileIndustriesOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Municipal
                    </Link>
                    <Link
                      to="/industries/industrial"
                      onClick={() => { setIsOpen(false); setMobileIndustriesOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Industrial
                    </Link>
                    <Link
                      to="/industries/hydropower"
                      onClick={() => { setIsOpen(false); setMobileIndustriesOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Hydropower
                    </Link>
                    <Link
                      to="/industries/mining"
                      onClick={() => { setIsOpen(false); setMobileIndustriesOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Mining & Quarrying
                    </Link>
                    <Link
                      to="/industries/aquaculture"
                      onClick={() => { setIsOpen(false); setMobileIndustriesOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Aquaculture & Fisheries
                    </Link>
                    <Link
                      to="/industries/environmental"
                      onClick={() => { setIsOpen(false); setMobileIndustriesOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Environmental
                    </Link>
                    <Link
                      to="/industries/desalination"
                      onClick={() => { setIsOpen(false); setMobileIndustriesOpen(false); }}
                      className="block text-gray-600 hover:text-ctc-blue hover:bg-white transition-colors py-2.5 px-6 text-sm"
                    >
                      Desalination & Water Reuse
                    </Link>
                  </motion.div>
                )}
              </div>

              {/* Blog */}
              <Link
                to="/blog"
                onClick={() => setIsOpen(false)}
                className="text-gray-700 hover:bg-gray-50 transition-colors font-medium text-left py-3 px-4 border-b border-gray-100"
              >
                Blog
              </Link>

              {/* Phone Number & Contact Button */}
              <div className="px-4 py-4 space-y-3 bg-gray-50/50">
                <div className="flex justify-center">
                  <PhoneNumber className="text-lg" />
                </div>

                <button
                  onClick={() => {
                    setIsContactModalOpen(true);
                    setIsOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-ctc-blue to-ctc-blue-dark text-white px-6 py-2.5 rounded-lg hover:from-ctc-blue-dark hover:to-ctc-blue transition-all duration-300 shadow-md text-center font-medium"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Unified Contact Modal */}
      <UnifiedContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)}
        productType="general"
        formSource="navbar_contact"
      />
    </motion.nav>
  );
};

export default Navbar;